/*
 * Formatting for the tenant console.
 *
 * Every function here used to be a local reimplementation, and each one was
 * wrong in the same way: hardcoded English and blind to the app's locale.
 * `formatRelative` returned "5m ago" to an Afrikaans tenant; `formatMinor`
 * returned "ZAR 5000.00" where South African formatting is "R5 000,00";
 * `formatDate` returned an ISO string; `formatDateTime` passed `undefined` as
 * the locale, which is the BROWSER's locale, not the one the user picked in
 * the app.
 *
 * They now delegate to `sdk-utils`, which resolves the locale from the app
 * store that `I18nProvider` keeps in sync with the language picker. Signatures
 * are unchanged so the ~40 call sites did not need touching.
 */
import {
  formatMinor as formatMinorIntl,
  formatCurrency as formatCurrencyIntl,
  formatNumber as formatNumberIntl
} from '@repletoryreach/sdk-utils/format';
import {
  formatRelative as formatRelativeIntl,
  formatInTz,
  getResolvedLocale
} from '@repletoryreach/sdk-utils/date';

/** Em dash stand-in for "no value". */
const EMPTY = '–';

function parse(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Date only, in the tenant's locale and timezone (e.g. "5 Sep 2026"). */
export function formatDate(iso: string | null | undefined): string {
  const d = parse(iso);
  return d ? formatInTz(d, { hour: undefined, minute: undefined }) : EMPTY;
}

/** Date and time, in the tenant's locale and timezone. */
export function formatDateTime(iso: string | null | undefined): string {
  const d = parse(iso);
  return d ? formatInTz(d) : EMPTY;
}

/** "5 minutes ago" / "5 minute gelede", per the chosen language. */
export function formatRelative(iso: string | null | undefined): string {
  const d = parse(iso);
  return d ? formatRelativeIntl(d, new Date(), getResolvedLocale()) : EMPTY;
}

export function shortId(id: string | null | undefined): string {
  if (!id) return EMPTY;
  return id.length > 10 ? `${id.slice(0, 8)}…` : id;
}

/** Minor-unit currency: 500000 ZAR minor → "R5 000,00" under en-ZA. */
export function formatMinor(minor: number, currency = 'ZAR'): string {
  return formatMinorIntl(minor, currency);
}

/** Major-unit currency, for inputs the user types in rands. */
export function formatCurrency(amount: number, currency = 'ZAR'): string {
  return formatCurrencyIntl(amount, currency);
}

/** Grouped integer: 2456 → "2 456" under en-ZA. */
export function formatCount(value: number): string {
  return formatNumberIntl(value);
}

// Re-exported so pages can pull `Indexed` from the format barrel.
export type { Indexed } from './types';
